import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Snackbar,
    Autocomplete,
    Paper,
    useTheme,
    alpha,
    IconButton,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import type { Theme } from '@mui/material/styles';
import { DataGridPremium, GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import InventoryIcon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BugReportIcon from '@mui/icons-material/BugReport';
import {
    getDevices,
    getDeviceStatusCounts,
    getShipments,
    getInstallations,
    GetDevicesParams,
    GetShipmentsParams,
    GetInstallationsParams,
} from '../lps.api';
import {
    LpsDevice,
    LpsShipment,
    LpsInstallation,
    LpsDeviceStatusCounts,
    DeviceStatus,
    InstallationStatus,
} from '../lps.types';
import LpsDeviceDetailDialog from '../components/LpsDeviceDetailDialog';
import LpsReceiveShipmentDialog from '../components/LpsReceiveShipmentDialog';
import LpsShipmentDetailDialog from '../components/LpsShipmentDetailDialog';
import LpsInstallationDialog from '../components/LpsInstallationDialog';
import LpsMqttDebugDialog from '../components/LpsMqttDebugDialog';
import { Organization } from 'utils';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { debounce } from 'lodash';

// ── Styled ───────────────────────────────────────────────────────────────

const FilterBar = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
}));

const SubTab = styled(Tab)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    minHeight: 42,
    '& .MuiTab-iconWrapper': {
        marginBottom: 0,
        marginRight: theme.spacing(0.75),
    },
}));

// ── Helpers ──────────────────────────────────────────────────────────────

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const formatDate = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '--';
const formatCost = (cents: number | null) =>
    cents != null ? `$${(cents / 100).toFixed(2)}` : '--';

const DEVICE_STATUS_COLORS: Record<
    DeviceStatus,
    'success' | 'info' | 'warning' | 'error' | 'default'
> = {
    in_stock: 'success',
    deployed: 'info',
    maintenance: 'warning',
    defective: 'error',
    returned: 'default',
    decommissioned: 'default',
};

const EMPTY_DEVICE_STATUS_COUNTS: LpsDeviceStatusCounts = {
    total: 0,
    in_stock: 0,
    deployed: 0,
    maintenance: 0,
    defective: 0,
    returned: 0,
    decommissioned: 0,
};

const DEVICE_STATUS_SUMMARY: { key: DeviceStatus; label: string }[] = [
    { key: 'in_stock', label: 'In Stock' },
    { key: 'deployed', label: 'Deployed' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'defective', label: 'Defective' },
    { key: 'returned', label: 'Returned' },
    { key: 'decommissioned', label: 'Decommissioned' },
];

const deviceStatusSummaryAccent = (theme: Theme, status: DeviceStatus): string => {
    switch (status) {
        case 'in_stock':
            return theme.palette.success.main;
        case 'deployed':
            return theme.palette.info.main;
        case 'maintenance':
            return theme.palette.warning.main;
        case 'defective':
            return theme.palette.error.main;
        case 'returned':
            return theme.palette.text.secondary;
        case 'decommissioned':
            return theme.palette.grey[600];
        default:
            return theme.palette.primary.main;
    }
};

const INSTALL_STATUS_COLORS: Record<InstallationStatus, 'info' | 'success' | 'default' | 'error'> =
    {
        planning: 'info',
        active: 'success',
        inactive: 'default',
        decommissioned: 'error',
    };

// ── Summary Cards ────────────────────────────────────────────────────────

interface SummaryCardProps {
    label: string;
    value: string | number;
    color?: string;
    icon?: React.ReactNode;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, color, icon }) => (
    <Paper
        variant="outlined"
        sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 140,
            borderColor: color ? alpha(color, 0.3) : 'divider',
            bgcolor: color ? alpha(color, 0.04) : 'transparent',
        }}
    >
        {icon}
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    </Paper>
);

// ═════════════════════════════════════════════════════════════════════════
// ── Main Component ───────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════

const LpsPage = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [snackMessage, setSnackMessage] = useState('');

    // ── Devices state ────────────────────────────────────────────────────
    const [devices, setDevices] = useState<LpsDevice[]>([]);
    /** Row count for the filtered device grid only (pagination), not global inventory */
    const [deviceFilteredRowCount, setDeviceFilteredRowCount] = useState(0);
    const [deviceStatusCounts, setDeviceStatusCounts] = useState<LpsDeviceStatusCounts>(
        EMPTY_DEVICE_STATUS_COUNTS,
    );
    const [deviceParams, setDeviceParams] = useState<GetDevicesParams>({ page: 1, per_page: 25 });
    const [devicePagination, setDevicePagination] = useState({ page: 0, pageSize: 25 });
    const [deviceSerialSearch, setDeviceSerialSearch] = useState('');
    const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('');
    const [deviceStatusFilter, setDeviceStatusFilter] = useState<string>('');
    const [deviceInstallationFilter, setDeviceInstallationFilter] = useState<string>('');
    const [deviceFilterInstallations, setDeviceFilterInstallations] = useState<LpsInstallation[]>(
        [],
    );
    const [selectedDevice, setSelectedDevice] = useState<LpsDevice | null>(null);
    const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);

    // ── Shipments state ──────────────────────────────────────────────────
    const [shipments, setShipments] = useState<LpsShipment[]>([]);
    const [totalShipments, setTotalShipments] = useState(0);
    const [shipmentPagination, setShipmentPagination] = useState({ page: 0, pageSize: 25 });
    const [shipmentSupplierSearch, setShipmentSupplierSearch] = useState('');
    const [receiveShipmentOpen, setReceiveShipmentOpen] = useState(false);
    const [shipmentDetailUuid, setShipmentDetailUuid] = useState<string | null>(null);
    const [shipmentDetailOpen, setShipmentDetailOpen] = useState(false);

    // ── Installations state ──────────────────────────────────────────────
    const [installations, setInstallations] = useState<LpsInstallation[]>([]);
    const [totalInstallations, setTotalInstallations] = useState(0);
    const [installationPagination, setInstallationPagination] = useState({ page: 0, pageSize: 25 });
    const [installStatusFilter, setInstallStatusFilter] = useState<string>('');
    const [installOrgFilter, setInstallOrgFilter] = useState<string>('');
    const [installDialogOpen, setInstallDialogOpen] = useState(false);
    const [selectedInstallation, setSelectedInstallation] = useState<LpsInstallation | null>(null);
    const [mqttDebugOpen, setMqttDebugOpen] = useState(false);
    const [mqttDebugInstallation, setMqttDebugInstallation] = useState<LpsInstallation | null>(
        null,
    );

    // ── Orgs autocomplete ────────────────────────────────────────────────
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchOrganizations = useCallback(
        debounce((searchTerm: string) => {
            adminGetAllOrganizations(searchTerm, 500)
                .then((res) => setOrganizations(res.data.data))
                .catch((err) => console.error('Failed to fetch orgs', err));
        }, 300),
        [],
    );

    useEffect(() => {
        fetchOrganizations('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDeviceFilterInstallations = useCallback(async () => {
        try {
            const merged: LpsInstallation[] = [];
            let page = 1;
            let lastPage = 1;
            do {
                const res = await getInstallations({ page, per_page: 50 });
                const batch = (res.data?.data ?? []) as LpsInstallation[];
                merged.push(...batch);
                lastPage = res.data?.meta?.last_page ?? 1;
                page++;
            } while (page <= lastPage);
            merged.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
            setDeviceFilterInstallations(merged);
        } catch (err) {
            console.error('Failed to fetch installations for device filter', err);
        }
    }, []);

    // ── Data fetching ────────────────────────────────────────────────────

    const fetchDevices = useCallback(async (params?: GetDevicesParams) => {
        try {
            const p = params || deviceParams;
            const res = await getDevices(p);
            setDevices(res.data?.data ?? []);
            setDeviceFilteredRowCount(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch devices', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDeviceStatusCounts = useCallback(async () => {
        try {
            const res = await getDeviceStatusCounts();
            const data = res.data?.data;
            if (data) {
                const sumByStatus =
                    (data.in_stock ?? 0) +
                    (data.deployed ?? 0) +
                    (data.maintenance ?? 0) +
                    (data.defective ?? 0) +
                    (data.returned ?? 0) +
                    (data.decommissioned ?? 0);
                setDeviceStatusCounts({
                    total: data.total ?? sumByStatus,
                    in_stock: data.in_stock ?? 0,
                    deployed: data.deployed ?? 0,
                    maintenance: data.maintenance ?? 0,
                    defective: data.defective ?? 0,
                    returned: data.returned ?? 0,
                    decommissioned: data.decommissioned ?? 0,
                });
            }
        } catch (err) {
            console.error('Failed to fetch device status counts', err);
        }
    }, []);

    const fetchShipments = useCallback(async (params?: GetShipmentsParams) => {
        try {
            const res = await getShipments(
                params || {
                    page: shipmentPagination.page + 1,
                    per_page: shipmentPagination.pageSize,
                    supplier_name: shipmentSupplierSearch || undefined,
                },
            );
            setShipments(res.data?.data ?? []);
            setTotalShipments(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch shipments', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchInstallations = useCallback(async (params?: GetInstallationsParams) => {
        try {
            const res = await getInstallations(
                params || {
                    page: installationPagination.page + 1,
                    per_page: installationPagination.pageSize,
                    status: installStatusFilter || undefined,
                    organization_uuid: installOrgFilter || undefined,
                },
            );
            setInstallations(res.data?.data ?? []);
            setTotalInstallations(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch installations', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchDevices({ page: 1, per_page: 25 });
        fetchShipments({ page: 1, per_page: 25 });
        fetchInstallations({ page: 1, per_page: 25 });
        fetchDeviceFilterInstallations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 0) {
            fetchDeviceStatusCounts();
        }
    }, [activeTab, fetchDeviceStatusCounts]);

    // ── Device search handler ────────────────────────────────────────────

    const handleDeviceSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params: GetDevicesParams = {
            page: 1,
            per_page: devicePagination.pageSize,
            status: deviceStatusFilter || undefined,
            device_type: deviceTypeFilter || undefined,
            installation_uuid: deviceInstallationFilter || undefined,
            serial_number: deviceSerialSearch || undefined,
        };
        setDeviceParams(params);
        setDevicePagination({ ...devicePagination, page: 0 });
        fetchDevices(params);
    };

    const handleDevicePaginationChange = (model: GridPaginationModel) => {
        setDevicePagination(model);
        const params = { ...deviceParams, page: model.page + 1, per_page: model.pageSize };
        setDeviceParams(params);
        fetchDevices(params);
    };

    // ── Shipment search handler ──────────────────────────────────────────

    const handleShipmentSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params: GetShipmentsParams = {
            page: 1,
            per_page: shipmentPagination.pageSize,
            supplier_name: shipmentSupplierSearch || undefined,
        };
        setShipmentPagination({ ...shipmentPagination, page: 0 });
        fetchShipments(params);
    };

    const handleShipmentPaginationChange = (model: GridPaginationModel) => {
        setShipmentPagination(model);
        fetchShipments({
            page: model.page + 1,
            per_page: model.pageSize,
            supplier_name: shipmentSupplierSearch || undefined,
        });
    };

    // ── Installation search handler ──────────────────────────────────────

    const handleInstallationSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params: GetInstallationsParams = {
            page: 1,
            per_page: installationPagination.pageSize,
            status: installStatusFilter || undefined,
            organization_uuid: installOrgFilter || undefined,
        };
        setInstallationPagination({ ...installationPagination, page: 0 });
        fetchInstallations(params);
    };

    const handleInstallationPaginationChange = (model: GridPaginationModel) => {
        setInstallationPagination(model);
        fetchInstallations({
            page: model.page + 1,
            per_page: model.pageSize,
            status: installStatusFilter || undefined,
            organization_uuid: installOrgFilter || undefined,
        });
    };

    // ── Column Definitions ───────────────────────────────────────────────

    const deviceColumns: GridColDef<LpsDevice>[] = [
        {
            field: 'serial_number',
            headerName: 'Serial Number',
            width: 180,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'device_type',
            headerName: 'Type',
            width: 110,
            renderCell: (params) => (
                <Chip
                    label={formatStatus(params.value)}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 140,
            renderCell: (params) => (
                <Chip
                    label={formatStatus(params.value)}
                    size="small"
                    color={DEVICE_STATUS_COLORS[params.value as DeviceStatus]}
                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
            ),
        },
        {
            field: 'installation_name',
            headerName: 'Installation',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const uuid = params.row.installation_uuid;
                const name = params.value as string | null | undefined;
                if (!uuid?.trim()) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            --
                        </Typography>
                    );
                }
                return (
                    <Typography variant="body2" noWrap title={name || undefined}>
                        {name?.trim() ? name : '--'}
                    </Typography>
                );
            },
        },
        { field: 'model', headerName: 'Model', width: 130, sortable: false },
        {
            field: 'unit_cost_cents',
            headerName: 'Cost',
            width: 90,
            sortable: false,
            renderCell: (params) => formatCost(params.value),
        },
        {
            field: 'received_at',
            headerName: 'Received',
            width: 130,
            renderCell: (params) => formatDate(params.value),
        },
        {
            field: 'deployed_at',
            headerName: 'Deployed',
            width: 130,
            renderCell: (params) => formatDate(params.value),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="View Details">
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedDevice(params.row);
                            setDeviceDialogOpen(true);
                        }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const shipmentColumns: GridColDef<LpsShipment>[] = [
        { field: 'supplier_name', headerName: 'Supplier', width: 200, sortable: false },
        { field: 'po_number', headerName: 'PO Number', width: 150, sortable: false },
        {
            field: 'received_at',
            headerName: 'Received',
            width: 140,
            renderCell: (params) => formatDate(params.value),
        },
        { field: 'received_by_name', headerName: 'Received By', width: 160, sortable: false },
        {
            field: 'total_cost_cents',
            headerName: 'Total Cost',
            width: 120,
            sortable: false,
            renderCell: (params) => formatCost(params.value),
        },
        {
            field: 'notes',
            headerName: 'Notes',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary" noWrap>
                    {params.value || '--'}
                </Typography>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="View Shipment">
                    <IconButton
                        size="small"
                        onClick={() => {
                            setShipmentDetailUuid(params.row.uuid);
                            setShipmentDetailOpen(true);
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const installationColumns: GridColDef<LpsInstallation>[] = [
        {
            field: 'name',
            headerName: 'Name',
            width: 280,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 140,
            renderCell: (params) => (
                <Chip
                    label={formatStatus(params.value)}
                    size="small"
                    color={INSTALL_STATUS_COLORS[params.value as InstallationStatus]}
                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
            ),
        },
        { field: 'location_address', headerName: 'Location', flex: 1, sortable: false },
        {
            field: 'installed_at',
            headerName: 'Installed',
            width: 130,
            renderCell: (params) => formatDate(params.value),
        },
        {
            field: 'gateway_uuid',
            headerName: 'Gateway',
            width: 100,
            sortable: false,
            renderCell: (params) =>
                params.value ? (
                    <Chip
                        label="Assigned"
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        --
                    </Typography>
                ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit Installation">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setSelectedInstallation(params.row);
                                setInstallDialogOpen(true);
                            }}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {params.row.status === 'active' && params.row.device_name && (
                        <Tooltip title="MQTT Debug">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setMqttDebugInstallation(params.row);
                                    setMqttDebugOpen(true);
                                }}
                            >
                                <BugReportIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ];

    const gridSx = {
        border: 'none',
        '& .MuiDataGrid-cell:focus': { outline: 'none' },
        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'action.hover',
            borderRadius: 0,
        },
        '& .MuiDataGrid-row:hover': {
            bgcolor: (t: any) => alpha(t.palette.primary.main, 0.04),
        },
    };

    return (
        <Box sx={{ p: 2.5 }}>
            <Snackbar
                open={!!snackMessage}
                autoHideDuration={5000}
                onClose={() => setSnackMessage('')}
                message={snackMessage}
            />

            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    LPS Hardware Inventory
                </Typography>
            </Box>

            {/* Sub-tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                    mb: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '& .MuiTabs-indicator': { height: 3, borderRadius: 1.5 },
                }}
            >
                <SubTab
                    icon={<InventoryIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Devices"
                />
                <SubTab
                    icon={<LocalShippingIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Shipments"
                />
                <SubTab
                    icon={<PlaceIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Installations"
                />
            </Tabs>

            {/* ═══ TAB 0: Devices ═══ */}
            {activeTab === 0 && (
                <>
                    {/* Summary */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                        <SummaryCard
                            label="Total Devices"
                            value={deviceStatusCounts.total}
                            icon={
                                <InventoryIcon
                                    sx={{ color: theme.palette.primary.main, fontSize: '1.5rem' }}
                                />
                            }
                            color={theme.palette.primary.main}
                        />
                        {DEVICE_STATUS_SUMMARY.map(({ key, label }) => (
                            <SummaryCard
                                key={key}
                                label={label}
                                value={deviceStatusCounts[key]}
                                color={deviceStatusSummaryAccent(theme, key)}
                            />
                        ))}
                    </Box>

                    {/* Filters */}
                    <FilterBar onSubmit={handleDeviceSearch}>
                        <TextField
                            size="small"
                            label="Serial Number"
                            value={deviceSerialSearch}
                            onChange={(e) => setDeviceSerialSearch(e.target.value)}
                            sx={{ width: 200 }}
                            placeholder="Search serial..."
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={deviceTypeFilter}
                                onChange={(e) => setDeviceTypeFilter(e.target.value)}
                                label="Type"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="anchor">Anchor</MenuItem>
                                <MenuItem value="tracker">Tracker</MenuItem>
                                <MenuItem value="gateway">Gateway</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={deviceStatusFilter}
                                onChange={(e) => setDeviceStatusFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="in_stock">In Stock</MenuItem>
                                <MenuItem value="deployed">Deployed</MenuItem>
                                <MenuItem value="maintenance">Maintenance</MenuItem>
                                <MenuItem value="defective">Defective</MenuItem>
                                <MenuItem value="returned">Returned</MenuItem>
                                <MenuItem value="decommissioned">Decommissioned</MenuItem>
                            </Select>
                        </FormControl>
                        <Autocomplete
                            value={
                                deviceFilterInstallations.find(
                                    (i) => i.uuid === deviceInstallationFilter,
                                ) || null
                            }
                            onChange={(_, v) => setDeviceInstallationFilter(v?.uuid || '')}
                            getOptionKey={(i) => i.uuid}
                            isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                            size="small"
                            options={deviceFilterInstallations}
                            getOptionLabel={(i) => `${i.name} (${formatStatus(i.status)})`}
                            renderInput={(params) => (
                                <TextField {...params} label="Installation" placeholder="All" />
                            )}
                            sx={{ minWidth: 260, maxWidth: 360 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="medium"
                        >
                            Search
                        </Button>
                    </FilterBar>

                    {/* Grid */}
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataGridPremium
                            rows={devices}
                            columns={deviceColumns}
                            getRowId={(row) => row.uuid}
                            rowCount={deviceFilteredRowCount}
                            pagination
                            paginationMode="server"
                            paginationModel={devicePagination}
                            onPaginationModelChange={handleDevicePaginationChange}
                            pageSizeOptions={[10, 25, 50, 100]}
                            disableColumnMenu
                            checkboxSelection={false}
                            autoHeight
                            sx={gridSx}
                            onRowClick={(params) => {
                                setSelectedDevice(params.row);
                                setDeviceDialogOpen(true);
                            }}
                        />
                    </Paper>
                </>
            )}

            {/* ═══ TAB 1: Shipments ═══ */}
            {activeTab === 1 && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <SummaryCard
                                label="Total Shipments"
                                value={totalShipments}
                                icon={
                                    <LocalShippingIcon
                                        sx={{ color: theme.palette.info.main, fontSize: '1.5rem' }}
                                    />
                                }
                                color={theme.palette.info.main}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setReceiveShipmentOpen(true)}
                        >
                            Receive Shipment
                        </Button>
                    </Box>

                    <FilterBar onSubmit={handleShipmentSearch}>
                        <TextField
                            size="small"
                            label="Supplier Name"
                            value={shipmentSupplierSearch}
                            onChange={(e) => setShipmentSupplierSearch(e.target.value)}
                            sx={{ width: 250 }}
                            placeholder="Search supplier..."
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="medium"
                        >
                            Search
                        </Button>
                    </FilterBar>

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataGridPremium
                            rows={shipments}
                            columns={shipmentColumns}
                            getRowId={(row) => row.uuid}
                            rowCount={totalShipments}
                            pagination
                            paginationMode="server"
                            paginationModel={shipmentPagination}
                            onPaginationModelChange={handleShipmentPaginationChange}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnMenu
                            checkboxSelection={false}
                            autoHeight
                            sx={gridSx}
                        />
                    </Paper>
                </>
            )}

            {/* ═══ TAB 2: Installations ═══ */}
            {activeTab === 2 && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <SummaryCard
                                label="Total Installations"
                                value={totalInstallations}
                                icon={
                                    <PlaceIcon
                                        sx={{
                                            color: theme.palette.success.main,
                                            fontSize: '1.5rem',
                                        }}
                                    />
                                }
                                color={theme.palette.success.main}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setSelectedInstallation(null);
                                setInstallDialogOpen(true);
                            }}
                        >
                            New Installation
                        </Button>
                    </Box>

                    <FilterBar onSubmit={handleInstallationSearch}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={installStatusFilter}
                                onChange={(e) => setInstallStatusFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="planning">Planning</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                                <MenuItem value="decommissioned">Decommissioned</MenuItem>
                            </Select>
                        </FormControl>
                        <Autocomplete
                            value={organizations.find((o) => o.uuid === installOrgFilter) || null}
                            onChange={(_, v) => setInstallOrgFilter(v?.uuid || '')}
                            onInputChange={(_, val, reason) => {
                                if (reason !== 'reset') fetchOrganizations(val);
                            }}
                            getOptionKey={(o) => o.uuid}
                            filterOptions={(options) => options}
                            size="small"
                            options={organizations}
                            getOptionLabel={(o) => `${o.name}`}
                            renderInput={(params) => <TextField {...params} label="Organization" />}
                            sx={{ width: 220 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="medium"
                        >
                            Search
                        </Button>
                    </FilterBar>

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataGridPremium
                            rows={installations}
                            columns={installationColumns}
                            getRowId={(row) => row.uuid}
                            rowCount={totalInstallations}
                            pagination
                            paginationMode="server"
                            paginationModel={installationPagination}
                            onPaginationModelChange={handleInstallationPaginationChange}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnMenu
                            checkboxSelection={false}
                            autoHeight
                            sx={gridSx}
                        />
                    </Paper>
                </>
            )}

            {/* ── Dialogs ─────────────────────────────────────────────────── */}
            <LpsDeviceDetailDialog
                open={deviceDialogOpen}
                device={selectedDevice}
                onClose={() => setDeviceDialogOpen(false)}
                onSave={() => {
                    setDeviceDialogOpen(false);
                    fetchDevices(deviceParams);
                    fetchDeviceStatusCounts();
                    setSnackMessage('Device updated');
                }}
            />
            <LpsReceiveShipmentDialog
                open={receiveShipmentOpen}
                onClose={() => setReceiveShipmentOpen(false)}
                onSuccess={() => {
                    setReceiveShipmentOpen(false);
                    fetchShipments();
                    fetchDevices(deviceParams);
                    fetchDeviceStatusCounts();
                    setSnackMessage('Shipment received successfully');
                }}
            />
            <LpsShipmentDetailDialog
                open={shipmentDetailOpen}
                shipmentUuid={shipmentDetailUuid}
                onClose={() => setShipmentDetailOpen(false)}
            />
            <LpsInstallationDialog
                open={installDialogOpen}
                installation={selectedInstallation}
                onClose={() => setInstallDialogOpen(false)}
                onSuccess={() => {
                    fetchInstallations();
                    fetchDeviceFilterInstallations();
                    fetchDevices(deviceParams);
                    fetchDeviceStatusCounts();
                    setSnackMessage(
                        selectedInstallation ? 'Installation updated' : 'Installation created',
                    );
                }}
            />
            {mqttDebugInstallation && (
                <LpsMqttDebugDialog
                    open={mqttDebugOpen}
                    onClose={() => {
                        setMqttDebugOpen(false);
                        setMqttDebugInstallation(null);
                    }}
                    installation={mqttDebugInstallation}
                />
            )}
        </Box>
    );
};

export default LpsPage;
